/**
 * Configuration constants for the concave globe visualization.
 */

import type { TileLayerType } from './types'

export const GLOBE_CONFIG = {
  // Geometry
  /** Radius of the hemisphere in world units */
  SPHERE_RADIUS: 10,
  /** Number of horizontal segments for geometry smoothness */
  WIDTH_SEGMENTS: 64,
  /** Number of vertical segments (half of width for hemisphere) */
  HEIGHT_SEGMENTS: 32,

  // Camera / FOV
  /** Default field of view in degrees */
  DEFAULT_FOV: 60,
  /** Minimum FOV (zoomed in, more detail) - lower allows more zoom */
  MIN_FOV: 3,
  /** Maximum FOV (zoomed out, overview) */
  MAX_FOV: 90,
  /** Near clipping plane (very close for interior viewing) */
  NEAR_CLIP: 0.1,
  /** Far clipping plane */
  FAR_CLIP: 100,

  // Tiles
  /** Tile size in pixels (Google Maps standard) */
  TILE_SIZE: 256,
  /** Maximum tile zoom level */
  MAX_ZOOM: 18,
  /** Minimum tile zoom level */
  MIN_ZOOM: 2,
  /** Default zoom level for browse mode */
  DEFAULT_ZOOM: 5,

  // Curvature morphing
  /** Full curvature when browsing (1.0 = hemisphere) */
  CURVATURE_BROWSE: 1.0,
  /** Reduced curvature for trip detail (0.0 = flat) */
  CURVATURE_DETAIL: 0.0,
  /** How quickly curvature transitions (0-1, higher = faster) */
  CURVATURE_LERP_FACTOR: 0.1,

  // Latitude constraints (Web Mercator limits)
  /** Maximum viewable latitude */
  MAX_LATITUDE: 85.051129,
  /** Minimum viewable latitude */
  MIN_LATITUDE: -85.051129,

  // Interaction
  /** Sensitivity multiplier for drag rotation */
  ROTATION_SENSITIVITY: 0.001,
  /** Sensitivity multiplier for scroll zoom */
  ZOOM_SENSITIVITY: 0.05,

  // Labels
  /** Distance labels are placed in front of globe surface (for concave viewing) */
  LABEL_RADIUS_OFFSET: 0.3,
  /** Maximum angular distance from center to show labels (degrees) */
  LABEL_MAX_VISIBLE_ANGLE: 80,

  // Tile Loading
  /** Maximum number of tiles to cache in memory */
  TILE_CACHE_SIZE: 500,
  /** Maximum concurrent tile load requests */
  TILE_MAX_CONCURRENT_LOADS: 150,
  /** Debounce delay for loading new tiles (ms) */
  TILE_LOAD_DEBOUNCE_MS: 200,
} as const

/**
 * Google Maps layer codes for different tile styles.
 */
export const TILE_LAYER_CODES: Record<TileLayerType, string> = {
  satellite: 's',
  street: 'm',
  hybrid: 'y',
  terrain: 't',
}

/**
 * Default tile layer type.
 */
export const DEFAULT_TILE_LAYER: TileLayerType = 'street'

/**
 * Get tile URL template for a given layer type.
 * {s} = server (0-3 for load balancing)
 * {x}, {y}, {z} = tile coordinates
 */
export function getTileUrlTemplate(layer: TileLayerType = DEFAULT_TILE_LAYER): string {
  return `https://mt{s}.google.com/vt/lyrs=${TILE_LAYER_CODES[layer]}&x={x}&y={y}&z={z}`
}

/**
 * Number of tile servers for load balancing (0-3).
 */
export const TILE_SERVERS = 4
